import {
  allCallingWeekdays,
  type CallingHours,
  type CallingWeekday,
} from "@repo/app-types/entities/teammateAgent";
import { toHumanReadableString } from "@repo/ui/helpers/helpers";
import { TimeSelect } from "@repo/ui/Select/timeSelect";
import { Switch } from "@repo/ui/Switch/switch";
import type { FC } from "react";
import {
  getCallingWindow,
  getTimezoneAbbreviation,
  minimumEndTime,
  setCallingDayEnabled,
  setCallingWindowTime,
} from "./callingHoursHelpers.js";

export interface CallingHoursEditorProps {
  hours: CallingHours;
  /** IANA zone the windows are expressed in (the company timezone). */
  timezone: string;
  isDisabled?: boolean;
  onChange: (hours: CallingHours) => void;
}

export const CallingHoursEditor: FC<CallingHoursEditorProps> = (props) => {
  const { hours, timezone, isDisabled, onChange } = props;

  const timezoneAbbreviation = getTimezoneAbbreviation(timezone);

  return (
    <div className="divide-y divide-border-light border-y border-border-light">
      {allCallingWeekdays.map((weekday) => (
        <CallingHoursDayRow
          key={weekday}
          weekday={weekday}
          hours={hours}
          timezoneAbbreviation={timezoneAbbreviation}
          isDisabled={isDisabled}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

interface CallingHoursDayRowProps {
  weekday: CallingWeekday;
  hours: CallingHours;
  timezoneAbbreviation: string;
  isDisabled?: boolean;
  onChange: (hours: CallingHours) => void;
}

const CallingHoursDayRow: FC<CallingHoursDayRowProps> = (props) => {
  const { weekday, hours, timezoneAbbreviation, isDisabled, onChange } = props;

  const window = getCallingWindow(hours, weekday);
  const dayLabel = toHumanReadableString(weekday);

  return (
    <div className="grid min-h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-2.5">
      <div className="flex items-center gap-3">
        <Switch
          isSelected={window !== undefined}
          onChange={(isEnabled) =>
            onChange(setCallingDayEnabled(hours, weekday, isEnabled))
          }
          isDisabled={isDisabled}
          accessibleLabel={`${window ? "Disable" : "Enable"} calls on ${dayLabel}`}
        />
        <span className="text-sm font-medium text-foreground">{dayLabel}</span>
      </div>

      {window ? (
        <div className="flex items-center gap-2">
          <TimeSelect
            value={window[0]}
            onChange={(time) =>
              onChange(setCallingWindowTime(hours, weekday, "start", time))
            }
            label={`${dayLabel} start time`}
            isLabelHidden
            isDisabled={isDisabled}
          />
          <span aria-hidden className="text-sm text-muted-foreground">
            to
          </span>
          <TimeSelect
            value={window[1]}
            onChange={(time) =>
              onChange(setCallingWindowTime(hours, weekday, "end", time))
            }
            minTime={minimumEndTime(window[0])}
            label={`${dayLabel} end time`}
            isLabelHidden
            isDisabled={isDisabled}
          />
          <span className="w-20 shrink-0 text-sm text-muted-foreground tabular-nums">
            {timezoneAbbreviation}
          </span>
        </div>
      ) : (
        <span className="pr-1 text-sm text-muted-foreground">No calls</span>
      )}
    </div>
  );
};
